// src/components/TermsOfUse.jsx

import React from "react";
import { Container, Typography, Box, List, ListItem, ListItemText } from "@mui/material";

const TermsOfUse = () => {
  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Terms of Use
      </Typography>

      <Typography variant="body2" sx={{ mb: 2 }}>
        <strong>Last Updated:</strong> [Date]
      </Typography>

      <Typography variant="body1" sx={{ mt: 2 }}>
        <strong>1. Acceptance of Terms</strong>
      </Typography>
      <Typography variant="body2" paragraph>
        Welcome to Chef Chatty, your friendly meal planner and cooking instructor. By accessing or using the App, you indicate that
        you have read, understand, and agree to be bound by these Terms of Use (“Terms”). If you do not agree
        with any part of these Terms, you may not access or use the App.
      </Typography>

      <Typography variant="body1" sx={{ mt: 2 }}>
        <strong>2. Description of Services</strong>
      </Typography>
      <Typography variant="body2" paragraph>
        Our App helps users plan meals by offering:
      </Typography>
      <List dense>
        <ListItem>
          <ListItemText
            primary="Conversational meal suggestions from our AI-powered assistant ('Chef Chatty')."
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="Personalized recipes tailored to user preferences and dietary restrictions."
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="A platform to save and manage favorite recipes and chat history."
          />
        </ListItem>
      </List>
      <Typography variant="body2" paragraph>
        We strive to keep the App’s content accurate and up to date; however, we make no guarantee that all 
        information is error-free, complete, or suited to every user’s needs.
      </Typography>

      <Typography variant="body1" sx={{ mt: 2 }}>
        <strong>3. Account Registration &amp; Security</strong>
      </Typography>
      <Box component="ol" sx={{ pl: 4 }}>
        <li>
          <Typography variant="body2">
            <strong>Registration:</strong> Some features require account creation. You must provide accurate,
            complete, and current information, including a valid email address.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            <strong>Email Confirmation:</strong> We may require you to confirm your email to activate your 
            account fully. If you do not confirm your email in the specified time, we may restrict access.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            <strong>Password Security:</strong> You are responsible for maintaining the confidentiality of 
            your account credentials. Immediately notify us of any unauthorized use or suspected breach.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            <strong>One Account:</strong> You agree to create and use only one account. Creating multiple 
            accounts may lead to account termination.
          </Typography>
        </li>
      </Box>

      <Typography variant="body1" sx={{ mt: 2 }}>
        <strong>4. User Responsibilities</strong>
      </Typography>
      <Box component="ol" sx={{ pl: 4 }}>
        <li>
          <Typography variant="body2">
            <strong>Meal &amp; Dietary Choices:</strong> The App’s meal suggestions and recipes are provided 
            for informational purposes only. Always verify the suitability of any recipes, especially if 
            you have medical or dietary concerns.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            <strong>Accurate Information:</strong> You are responsible for providing correct information 
            about dietary restrictions, allergies, or health conditions.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            <strong>User-Generated Content:</strong> If the App allows the posting or sharing of content 
            (e.g., custom recipes, comments), you agree that your content is lawful, does not infringe 
            any third-party rights, and does not contain harmful or offensive material.
          </Typography>
        </li>
      </Box>

      <Typography variant="body1" sx={{ mt: 2 }}>
        <strong>5. Intellectual Property</strong>
      </Typography>
      <Box component="ol" sx={{ pl: 4 }}>
        <li>
          <Typography variant="body2">
            <strong>Our Content:</strong> All features of the App, including text, images, AI-generated 
            content (where not user-submitted), logos, and design, are owned by or licensed to us. You may 
            not copy, distribute, or modify this content without written permission.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            <strong>User Content:</strong> You retain ownership of any content you submit. By submitting 
            content, you grant us a worldwide, non-exclusive, royalty-free license to use, display, and 
            distribute that content within the App’s functionality.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            <strong>Trademarks:</strong> “Chef Chatty” and any associated logos are our 
            trademarks. You may not use them without our permission.
          </Typography>
        </li>
      </Box>

      <Typography variant="body1" sx={{ mt: 2 }}>
        <strong>6. Disclaimers</strong>
      </Typography>
      <Box component="ol" sx={{ pl: 4 }}>
        <li>
          <Typography variant="body2">
            <strong>No Medical Advice:</strong> We are <em>not</em> providing medical or dietary advice. Always 
            consult a qualified professional before making significant changes to your diet, especially if 
            you have health conditions.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            <strong>Nutritional Accuracy:</strong> While we try to consider dietary preferences and allergies, 
            the App’s suggestions or AI responses may not always be accurate or suitable for your needs. 
            You are solely responsible for verifying the safety of any ingredients and recipes.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            <strong>Availability:</strong> We do not guarantee the App will always be available, free of errors, 
            or secure. Our services may be interrupted by maintenance or technical issues.
          </Typography>
        </li>
      </Box>

      <Typography variant="body1" sx={{ mt: 2 }}>
        <strong>7. Limitation of Liability</strong>
      </Typography>
      <Typography variant="body2" paragraph>
        <strong>To the fullest extent permitted by law</strong>, we and our affiliates, officers, employees, 
        and agents shall not be liable for any indirect, incidental, special, consequential, or punitive 
        damages, or any loss of profits or revenues, incurred directly or indirectly from:
      </Typography>
      <List dense>
        <ListItem>
          <ListItemText primary="Your use of (or inability to use) the App" />
        </ListItem>
        <ListItem>
          <ListItemText primary="Any errors or omissions in the App’s content or AI suggestions" />
        </ListItem>
        <ListItem>
          <ListItemText primary="Unauthorized access or misuse of your account" />
        </ListItem>
        <ListItem>
          <ListItemText primary="Any issues relating to the ingredients or recipes recommended by the App" />
        </ListItem>
      </List>
      <Typography variant="body2" paragraph>
        If, notwithstanding the above, liability is found against us, our total liability shall in no event 
        exceed the fees (if any) paid by you for accessing the App within the previous six months.
      </Typography>

      <Typography variant="body1" sx={{ mt: 2 }}>
        <strong>8. User Account Termination</strong>
      </Typography>
      <Box component="ol" sx={{ pl: 4 }}>
        <li>
          <Typography variant="body2">
            <strong>Voluntary Termination:</strong> You may delete your account at any time through 
            the “Account Settings” or by contacting us.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            <strong>Involuntary Termination:</strong> We reserve the right to suspend or terminate your 
            account if we suspect violation of these Terms, unlawful activity, or any behavior that 
            threatens the security or integrity of the App.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            <strong>Data Deletion:</strong> Upon account termination, we may delete your personal data 
            and saved recipes, though residual copies may remain in backups for a limited time.
          </Typography>
        </li>
      </Box>

      <Typography variant="body1" sx={{ mt: 2 }}>
        <strong>9. Governing Law &amp; Dispute Resolution</strong>
      </Typography>
      <Typography variant="body2" paragraph>
        These Terms shall be governed by and construed in accordance with the laws of the District of Columbia. 
        Any dispute arising from or relating to these Terms shall be exclusively brought in the courts of 
        District of Columbia, and you consent to the jurisdiction and venue of such courts.
      </Typography>

      <Typography variant="body1" sx={{ mt: 2 }}>
        <strong>10. Modifications to Terms</strong>
      </Typography>
      <Typography variant="body2" paragraph>
        We may update these Terms from time to time. Any significant change will be posted or otherwise 
        communicated to you. By continuing to use the App after the changes become effective, you agree 
        to be bound by the revised Terms.
      </Typography>

      <Typography variant="body1" sx={{ mt: 2 }}>
        <strong>11. Contact Us</strong>
      </Typography>
      <Typography variant="body2" paragraph>
        If you have any questions or concerns about these Terms or the Meal Planner application, please 
        contact us at <strong><a href="mailto:support@chefchatty.com">support@chefchatty.com</a></strong>.
      </Typography>
    </Container>
  );
};

export default TermsOfUse;
